import { GroupTypes } from "@custom-types/group-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { Breadcrumb, Breadcrumbs } from "@flat-ui";
import { RouteParams } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { getGroupByIdSelector } from "@store/groups/groups-selector";
import { companyMemberSelector } from "@store/members/members-selector";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { selectedCompanyContextSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { getTeamByIdSelector } from "@store/teams/teams-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { BreadcrumbHandle } from "@utils/sphere-breadcrumb-utils";
import { BreadcrumbEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo } from "react";
import { useLocation, useMatches, useNavigate } from "react-router-dom";

/** Extended definition when using useMatches to provide types to the attributes that we know. */
interface MatchObject<HandleT> extends ReturnType<typeof useMatches> {
  /** id of the route, supplied by react-router */
  id: string;
  /** the path of the matched route, defined by us in the routes */
  pathname: string;
  /** route params, defined in the routes */
  params: Partial<RouteParams>;
  /** the data object, which contains data returned by the loader of the route */
  data: unknown;
  /** a handle that includes the breadcrumb for the route  as the defined by the type BreadcrumbHandle */
  handle: HandleT;
}

/** Defines the data included to the breadcrumb handle. */
export interface BreadcrumbHandleData {
  /** Includes the selected project object if applicable to the route, null otherwise. */
  selectedProject: SdbProject | null;
  /** Includes the selected group object if applicable to the route.*/
  selectedGroup?: GroupTypes;
  /** Includes the selected company member if applicable to the route */
  selectedMember?: SdbMemberTypes | null;
  /** Includes the selected team if applicable to the route */
  selectedTeam?: FaroCoreApiTypes.Team | null;
  /** Includes the current user. */
  currentUser?: FaroCoreApiTypes.CompanyMemberBase | null;
  /**
   * Includes the data coming in the route from useLoaderData
   *
   * @see https://reactrouter.com/en/main/hooks/use-loader-data
   */
  loaderData?: unknown;
  /** Get the query params for the breadcrumbs */
  queryParams?: string;
  /** List of features available in company context */
  companyContext?: FaroCoreApiTypes.GetCompanyContext | null;
}

/**
 * SphereBreadcrumbs – wrapper around Breadcrumbs that builds the correct
 * breadcrumb list from React Router matches + app state.
 */
export function SphereBreadcrumbs(): JSX.Element {
  const matches = useMatches() as Array<MatchObject<BreadcrumbHandle>>;
  const location = useLocation();
  const queryParams = location.search;
  const navigate = useNavigate();

  const { trackEvent } = useTrackEvent();
  const { projectId, groupId, memberId, teamId } = useAppParams();

  const selectedProject = useAppSelector(getProjectByIdSelector(projectId));
  const selectedGroup = useAppSelector(getGroupByIdSelector(groupId));
  const selectedMember = useAppSelector(companyMemberSelector(memberId));
  const selectedTeam = useAppSelector(getTeamByIdSelector(teamId));
  const currentUser = useAppSelector(currentUserSelector);
  const companyContext = useAppSelector(selectedCompanyContextSelector);

  /** Build the breadcrumbs array from matches */
  const breadcrumbs: Breadcrumb[] = useMemo(() => {
    return (
      matches
        .filter((m) => Boolean(m.handle?.breadcrumb))
        .map((m) =>
          typeof m.handle.breadcrumb === "function"
            ? m.handle.breadcrumb(m.params, {
                selectedProject,
                selectedGroup,
                selectedMember,
                selectedTeam,
                currentUser,
                loaderData: m.data,
                queryParams,
                companyContext,
              })
            : m.handle.breadcrumb
        )
        .flat()
        .filter((b) => Boolean(b?.text)) as Breadcrumb[]
    );
  }, [
    matches,
    currentUser,
    selectedGroup,
    queryParams,
    selectedProject,
    selectedMember,
    selectedTeam,
    companyContext,
  ]);

  /** Track clicks */
  const handleBreadcrumbClick = useCallback(
    (breadcrumb: Breadcrumb) => {
      trackEvent({ name: BreadcrumbEvents.navigateWithBreadcrumb });
      if (breadcrumb.link) {
        navigate(breadcrumb.link);
      }
    },
    [navigate, trackEvent]
  );

  return (
    <Breadcrumbs
      breadcrumbs={breadcrumbs}
      onBreadcrumbClick={handleBreadcrumbClick}
    />
  );
}
